import { Inject, Injectable } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { Meta, Title } from "@angular/platform-browser";
import { ActivatedRouteSnapshot } from "@angular/router";

/** SEO settings of a page, declared in a route's `data.seo` or set by a component */
export interface SeoData {
  title?: string;
  description?: string;
  image?: string;
  /** true for private or transactional pages (cart, account...) that must not be indexed */
  noindex?: boolean;
  /** true when the page component sets its own SEO once its data is loaded (product, category...) */
  dynamic?: boolean;
}

export const SITE_NAME = "Wissal Univers";
/** production domain, used for canonical URLs so staging/duplicate URLs point to the real site */
export const SITE_URL = "https://www.wiunivers.com";

const DEFAULT_SEO: SeoData = {
  description:
    "Wissal Univers : informatique, smartphones, électroménager et meubles, en cash ou par facilités. En ligne ou dans nos 5 showrooms en Algérie.",
  image: `${SITE_URL}/assets/media/images/WU_Stores.jpg`,
};

@Injectable({ providedIn: "root" })
export class SeoService {
  constructor(
    private meta: Meta,
    private titleService: Title,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  /**
   * Applies the SEO declared in the activated routes (`data.seo`, children overriding parents).
   * Pages flagged `dynamic` only get their canonical URL here: their component calls `set()` itself.
   */
  applyRoute(root: ActivatedRouteSnapshot, url: string): void {
    let seo: SeoData = {};
    let route: ActivatedRouteSnapshot | null = root;
    while (route) {
      seo = { ...seo, ...(route.data?.["seo"] ?? {}) };
      route = route.firstChild;
    }
    this.setCanonical(url);
    if (!seo.dynamic) {
      this.set(seo, url);
    }
  }

  /** Sets the title, description, robots, Open Graph and canonical tags of the current page */
  set(seo: SeoData, url?: string): void {
    const title = seo.title ? `${seo.title} | ${SITE_NAME}` : SITE_NAME;
    const description = this.clean(seo.description) || DEFAULT_SEO.description!;
    const image = seo.image || DEFAULT_SEO.image!;

    this.titleService.setTitle(title);
    this.meta.updateTag({ name: "description", content: description });
    this.meta.updateTag({
      name: "robots",
      content: seo.noindex ? "noindex, nofollow" : "index, follow",
    });

    this.meta.updateTag({ property: "og:site_name", content: SITE_NAME });
    this.meta.updateTag({ property: "og:type", content: "website" });
    this.meta.updateTag({ property: "og:title", content: title });
    this.meta.updateTag({ property: "og:description", content: description });
    this.meta.updateTag({ property: "og:image", content: image });
    this.meta.updateTag({ name: "twitter:card", content: "summary_large_image" });

    if (url) {
      this.setCanonical(url);
    }
  }

  /** Points the canonical link and og:url to the production URL of the page, without query params */
  setCanonical(url: string): void {
    const path = url.split(/[?#]/)[0];
    const href = SITE_URL + (path === "/" ? "/home" : path);
    let link = this.document.head.querySelector<HTMLLinkElement>(
      "link[rel='canonical']",
    );
    if (!link) {
      link = this.document.createElement("link");
      link.setAttribute("rel", "canonical");
      this.document.head.appendChild(link);
    }
    link.setAttribute("href", href);
    this.meta.updateTag({ property: "og:url", content: href });
  }

  /** Adds (or replaces) a JSON-LD structured data block, identified by `id`, in the page head */
  setJsonLd(id: string, data: object): void {
    this.removeJsonLd(id);
    const script = this.document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", id);
    script.text = JSON.stringify(data);
    this.document.head.appendChild(script);
  }

  /** Removes a JSON-LD block added with `setJsonLd`, to call when leaving the page */
  removeJsonLd(id: string): void {
    this.document.head
      .querySelectorAll(`script[data-seo='${id}']`)
      .forEach((script) => script.remove());
  }

  /**
   * Publishes a breadcrumb trail as schema.org `BreadcrumbList`, so search results show the category path.
   * Relative urls are resolved against the production site; "./" or a missing url means the current page.
   */
  setBreadcrumbs(items: { label: string; url?: string }[]): void {
    const links = items.filter((item) => item.label);
    if (!links.length) {
      this.removeJsonLd("breadcrumbs");
      return;
    }
    this.setJsonLd("breadcrumbs", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: links.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.label,
        item: this.absoluteUrl(item.url),
      })),
    });
  }

  /** Resolves an app url ("/", "/#/...", "./", relative path) to its production URL */
  absoluteUrl(url?: string): string {
    if (url && /^https?:\/\//.test(url)) {
      return url;
    }
    let path = (url ?? "./").replace(/^\/#/, "");
    if (path === "./" || path === "") {
      path = this.document.location?.pathname ?? "/";
    }
    if (!path.startsWith("/")) {
      path = "/" + path;
    }
    return SITE_URL + (path === "/" ? "/home" : path);
  }

  /** Strips HTML from rich-text descriptions and cuts them to the ~160 chars shown by search engines */
  private clean(text?: string): string {
    if (!text) {
      return "";
    }
    const plain = text
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return plain.length > 160 ? plain.slice(0, 157).trimEnd() + "..." : plain;
  }
}
